import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import swal from 'sweetalert2'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { getCurrencyInfo } from '@/util/currencyFormat'


export default /*#__PURE__*/_defineComponent({
  __name: 'PassTypeDrawer',
  props: {
  show: { type: Boolean, default: false },
  passType: { type: Object, default: null }
},
  emits: ['update:show', 'close', 'saved'],
  setup(__props, { emit }) {

const props = __props





const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const currencySymbol = getCurrencyInfo().symbol

const durationUnits = [
  { value: 'day', singular: 'Day', plural: 'Days' },
  { value: 'week', singular: 'Week', plural: 'Weeks' },
  { value: 'month', singular: 'Month', plural: 'Months' },
  { value: 'year', singular: 'Year', plural: 'Years' }
]
const allowanceOptions = [
  { value: 'per_booking', label: 'Per booking' },
  { value: 'total', label: 'Total for the pass' }
]

const isEdit = computed(() => !!(props.passType && props.passType.id))

const saving = ref(false)
const availableGames = ref<any[]>([])
const gamesLoaded = ref(false)

function defaultForm() {
  return {
    name: '',
    internal_label: '',
    description: '',
    duration_value: 1,
    duration_unit: 'month',
    allowance_type: 'per_booking',
    ticket_allowance: 1,
    applies_to_all_games: true,
    linked_game_ids: [] as number[],
    is_purchasable: false,
    price: undefined as number | undefined
  }
}

const form = ref(defaultForm())

function loadForm() {
  if (props.passType && props.passType.id) {
    const pt = props.passType
    form.value = {
      name: pt.name || '',
      internal_label: pt.internal_label || '',
      description: pt.description || '',
      duration_value: pt.duration_value,
      duration_unit: pt.duration_unit,
      allowance_type: pt.allowance_type,
      ticket_allowance: pt.ticket_allowance,
      applies_to_all_games: !!pt.applies_to_all_games,
      linked_game_ids: (pt.games || []).map((g: any) => g.id),
      is_purchasable: !!pt.is_purchasable,
      price: pt.price != null ? Number(pt.price) : undefined
    }
  } else {
    form.value = defaultForm()
  }
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function getGames() {
  if (gamesLoaded.value) return
  try {
    const config = await authConfig()
    const response = await axios.get(`${baseUrl}/games`, config)
    const games = Array.isArray(response.data) ? response.data : (response.data.games || [])
    availableGames.value = games
      .filter((g: any) => !g.archived)
      .map((g: any) => ({ id: g.id, name: g.internal_name || g.name }))
    gamesLoaded.value = true
  } catch (err) {
    console.log(err)
  }
}

function validate(): string | null {
  if (!form.value.name || !form.value.name.trim()) return 'Name is required'
  if (!form.value.duration_value || form.value.duration_value < 1) return 'Duration must be at least 1'
  if (!form.value.ticket_allowance || form.value.ticket_allowance < 1) return 'Ticket allowance must be at least 1'
  if (!form.value.applies_to_all_games && form.value.linked_game_ids.length === 0) {
    return 'Select at least one game or enable all games'
  }
  if (form.value.is_purchasable && (!form.value.price || form.value.price <= 0)) {
    return 'A price greater than zero is required to sell online'
  }
  return null
}

async function save() {
  const validationMessage = validate()
  if (validationMessage) {
    proxy.$notify({ type: 'warning', message: validationMessage, icon: 'tim-icons icon-alert-circle-exc' })
    return
  }

  saving.value = true
  try {
    const config = await authConfig()
    const payload = {
      name: form.value.name.trim(),
      internal_label: form.value.internal_label || null,
      description: form.value.description || null,
      duration_value: form.value.duration_value,
      duration_unit: form.value.duration_unit,
      allowance_type: form.value.allowance_type,
      ticket_allowance: form.value.ticket_allowance,
      applies_to_all_games: form.value.applies_to_all_games ? 1 : 0,
      linked_game_ids: form.value.applies_to_all_games ? [] : form.value.linked_game_ids,
      is_purchasable: form.value.is_purchasable ? 1 : 0,
      price: form.value.is_purchasable ? form.value.price : null
    }

    if (isEdit.value) {
      await axios.put(`${baseUrl}/pass-types/${props.passType.id}`, payload, config)
    } else {
      await axios.post(`${baseUrl}/pass-types`, payload, config)
    }

    proxy.$notify({
      type: 'success',
      message: isEdit.value ? 'Pass type saved' : 'Pass type created',
      icon: 'tim-icons icon-check-2'
    })
    emit('saved')
    emit('update:show', false)
    emit('close')
  } catch (err: any) {
    // New lambdas return JSON errors; surface field validation when present
    let message = 'The pass type could not be saved'
    const data = err?.response?.data
    if (data && Array.isArray(data.validation_errors) && data.validation_errors.length > 0) {
      message = data.validation_errors[0].message
    } else if (data && data.error) {
      message = data.error
    }
    swal('Error!', message, 'error')
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
    getGames()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})

return { __sfc: true,props, emit, proxy, companyId, companyGroupId, baseUrl, currencySymbol, durationUnits, allowanceOptions, isEdit, saving, availableGames, gamesLoaded, defaultForm, form, loadForm, authConfig, getGames, validate, save, onClose, onEscKey }
}

})